import { isBusinessOpen, primaryProperty, useGetAllBusinesses } from "@api/businessService";
import { useEffect, useState } from "react";
import Box from '@mui/joy/Box';
import IconButton from '@mui/joy/IconButton';
import MenuItem from '@mui/joy/MenuItem';
import Tooltip from '@mui/joy/Tooltip';
import Typography from '@mui/joy/Typography';
import ObjectIndex from "@components/objects/ObjectIndex";
import LocalPhoneRoundedIcon from '@mui/icons-material/LocalPhoneRounded';
import EmailRoundedIcon from '@mui/icons-material/EmailRounded';
import { formatDate } from "@utils/dates";
import { useGetAllProperties } from "@api/propertyService";
import ContentCopyRoundedIcon from '@mui/icons-material/ContentCopyRounded';
import DirectionsRoundedIcon from '@mui/icons-material/DirectionsRounded';
import { useDeleteOpportunity, useGetAllOpportunities } from "@api/opportunityService";
import { useCopyToClipboard } from "@uidotdev/usehooks";
import { Button, LinearProgress, Stack } from "@mui/joy";
import toast from "react-hot-toast";
import { CopyLinkButton } from "@components/CopyLinkButton";
import { OpportunityCloneModal } from "@components/OpportunityCloneModal";

const columns = [
    {
        id: 'name',
        numeric: false,
        disablePadding: true,
        sortable: true,
        label: 'Name',
        cell: (row, {LinkComponent}) => {
            return (<>
                <LinkComponent to={`${row.id}`} relative={true} object={row}>
                    <Typography sx={{display: 'inline-block'}} fontWeight={'bold'}>{row.name}</Typography>
                </LinkComponent>
            </>)
        }
    },
    {
        id: 'filled',
        numeric: false,
        disablePadding: false,
        sortable: false,
        label: 'Filled',
        cell: (row) => {
            const totalJobShifts = row.jobs.reduce((count, job) => {
                return (job.quantity * job.available_time_slots.length) + count;
            }, 0);

            const assignedJobShifts = row.sign_ups.length;
            const progress = totalJobShifts ? Math.min(((assignedJobShifts / totalJobShifts) * 100), 100) : 0;

            return (
                <Stack
                  direction="row"
                  justifyContent="flex-start"
                  alignItems="center"
                  spacing={2}
                >
                <Box sx={{flex: '0 1 25%', display: 'flex', justifyContent: 'flex-end'}}>
                        <LinearProgress determinate value={progress} sx={{backgroundColor: 'var(--joy-palette-background-backdrop)'}} />
                    </Box>
                    <Box sx={{flex: 'auto', whiteSpace: 'nowrap'}}>
                        <Tooltip title={'Completed Tasks'}>
                            <Box sx={{display: 'inline'}}>{assignedJobShifts}</Box>
                        </Tooltip>
                        {' of '}
                        <Tooltip title={'Total Tasks'}>
                            <Box sx={{display: 'inline'}}>{totalJobShifts}</Box>
                        </Tooltip>
                    </Box>

                </Stack>
            )
        }
      },
    {
        id: 'attached',
        numeric: false,
        disablePadding: false,
        sortable: false,
        label: 'Attached',
        cell: (row, {LinkComponent}) => {
            if(!row.event && !row.workplan) {
                return null;
            }

            return (<>
                {row.event && (
                    <Typography fontWeight={'bold'}>
                        Event{' '}
                        <LinkComponent sx={{display: 'inline-block'}} to={`/events/${row.event.id}`} relative={true} object={row.event}>
                            <Typography sx={{display: 'inline-block'}} fontWeight={'bold'}>{row.event.name}</Typography>
                        </LinkComponent>
                    </Typography>
                )}
                {row.workplan && (
                    <Typography fontWeight={'bold'}>
                        Work Plan{' '}
                        <LinkComponent sx={{display: 'inline-block'}} to={`/work-plans/${row.workplan.id}`} relative={true} object={row.workplan}>
                            <Typography sx={{display: 'inline-block'}} fontWeight={'bold'}>{row.workplan.name}</Typography>
                        </LinkComponent>
                    </Typography>
                )}
            </>
            );
        }
    },
    {
        id: 'link',
        numeric: false,
        disablePadding: true,
        sortable: false,
        label: 'Link',
        // width: '15%',
        showOnDeleted: false,
        cell: (row, {LinkComponent}) => {
            return (
                <CopyLinkButton link={row?.full_link}/>
            )
        }
    },
];

const listCell = (row, {LinkComponent}) => {
    return (<>
        <Box sx={{mb: 1}}>
            <Typography sx={{display: 'inline-block'}} fontWeight={'bold'}>
                <LinkComponent to={`${row.id}`} relative={true} object={row}>
                    {row.name}
                </LinkComponent>
            </Typography>
        </Box>
    </>);
};

const listActions = (row, {view, LinkComponent, ViewAction}) => {
    if(view === 'deleted') {
        return null;
    }

    return (<>
        <CopyLinkButton link={row?.full_link} includeText={false} />
    </>);
};

const OpportunityCloneButton = ({data, open, setOpen, ...restProps}) => {
    return (<>
        <Tooltip title="Clone an Opportunity">
            <IconButton onClick={() => setOpen(true)} sx={{ bgcolor: 'background.surface' }} {...restProps} data-first-child>
                <ContentCopyRoundedIcon/>
            </IconButton>
        </Tooltip>
    </>)
};

const OpportunityCloneMenuItem = ({row, open, setOpen, ...restProps}) => {
    return (<>
        <MenuItem onClick={() => setOpen(true, row)}>Clone</MenuItem>
    </>)
};

export default function OpportunityIndex() {
    const [cloningOpen, _unstable_setCloningOpen] = useState(false);
    const [cloningOpportunity, setCloningOpportunity] = useState(null);
    const setCloningOpen = (state, opportunity = null) => {
        setCloningOpportunity(opportunity);
        _unstable_setCloningOpen(state);
    }

    return (<>
        <ObjectIndex
            query={useGetAllOpportunities}
            deleteMutation={useDeleteOpportunity}
            type={'opportunity'}
            permissionType={'work plans'}
            title={'Opportunities'}
            color={'default'}
            columns={columns}
            listCell={listCell}
            listActions={listActions}
            additionalHeaderActions={(({data, ...restProps}) => (<><OpportunityCloneButton data={data} open={cloningOpen} setOpen={setCloningOpen} {...restProps} /></>))}
            additionalMoreActions={(({row, ...restProps}) => (<><OpportunityCloneMenuItem row={row} open={cloningOpen} setOpen={setCloningOpen} {...restProps} /></>))}
        />
        <OpportunityCloneModal opportunity={cloningOpportunity} open={cloningOpen} setOpen={setCloningOpen} />
    </>)
}
